document.addEventListener('alpine:init', () => {
  Alpine.data('CtrPurchaseBoxProduct', (sectionId) => ({
    sectionId,
    product: null,
    variantMetafieldsById: {},
    optionGroups: [],
    selectedOptions: {},
    selectedVariant: null,
    typePurchase: 'one-time',
    mainSlider: null,
    thumbnailSlider: null,
    firstImageVariant: false,
    modalOpen: false,
    imgModalSrc: '',

    debug(label, payload) {
      console.log(`[ctr-isolate-pdb][product][${this.sectionId}] ${label}`, payload);
    },

    init() {
      this.debug('init:start', {
        sectionId: this.sectionId,
        refs: {
          productDataJson: !!this.$refs.productDataJson,
          productOptionsWithValuesJson: !!this.$refs.productOptionsWithValuesJson,
          productVariantsMetafieldJson: !!this.$refs.productVariantsMetafieldJson,
        },
      });

      this.parseProductData();
      if (!this.product || !Array.isArray(this.product.variants)) {
        this.debug('init:abort-invalid-product', {
          product: this.product,
          variantsIsArray: Array.isArray(this.product?.variants),
        });
        return;
      }

      this.firstImageVariant = this.$el.dataset.firstImageVariant === 'true';
      this.typePurchase = (this.product?.selling_plan_groups || []).length ? 'subscribe' : 'one-time';
      this.debug('init:after-parse', {
        firstImageVariant: this.firstImageVariant,
        typePurchase: this.typePurchase,
        productSummary: {
          id: this.product.id,
          handle: this.product.handle,
          title: this.product.title,
          options: this.product.options,
          optionsWithValuesCount: this.productOptionsWithValues?.length || 0,
          variantsCount: this.product.variants.length,
          sellingPlanGroupsCount: this.product.selling_plan_groups?.length || 0,
          selectedOrFirstVariantId: this.product.selected_or_first_available_variant_id || null,
        },
      });

      this.buildOptionGroups();
      this.selectInitialVariant();
      this.initSlider();
      this.stickyBanner();
      this.broadcastVariantChange();
      window.addEventListener('ctr-isolate-purchase-box:purchase-type-change', (event) => {
        if (event.detail.sectionId !== this.sectionId) return;
        this.typePurchase = event.detail.typePurchase;
        this.debug('event:purchase-type-change', event.detail);
      });
      this.debug('init:complete', {
        optionGroups: this.optionGroups,
        selectedOptions: this.selectedOptions,
        selectedVariant: this.selectedVariant,
        typePurchase: this.typePurchase,
      });
    },

    parseProductData() {
      try {
        this.product = JSON.parse(this.$refs.productDataJson.textContent);
        this.debug('parseProductData:product-json', this.product);
      } catch (error) {
        console.error('ctr-isolate-purchase-box: failed to parse product JSON', error);
        this.product = null;
      }

      try {
        this.productOptionsWithValues = JSON.parse(this.$refs.productOptionsWithValuesJson.textContent) || [];
        this.debug('parseProductData:options-with-values-json', this.productOptionsWithValues);
      } catch (error) {
        console.error('ctr-isolate-purchase-box: failed to parse product options JSON', error);
        this.productOptionsWithValues = [];
      }

      try {
        const metafields = JSON.parse(this.$refs.productVariantsMetafieldJson.textContent);
        this.variantMetafieldsById = Object.fromEntries(
          (metafields || []).map((entry) => [String(entry.id), entry.metafields || {}])
        );
        this.debug('parseProductData:variant-metafields-json', metafields);
        this.debug('parseProductData:variant-metafields-map', this.variantMetafieldsById);
      } catch (error) {
        console.error('ctr-isolate-purchase-box: failed to parse variant metafields JSON', error);
        this.variantMetafieldsById = {};
      }
    },

    buildOptionGroups() {
      const sourceGroups =
        this.productOptionsWithValues?.length
          ? this.productOptionsWithValues
          : (this.product?.options || []).map((name, index) => ({
              name,
              values: [
                ...new Set(
                  (this.product?.variants || [])
                    .map((variant) => variant.options?.[index])
                    .filter(Boolean)
                ),
              ],
            }));
      this.debug('buildOptionGroups:source-groups', sourceGroups);
      this.optionGroups = sourceGroups.map((group, index) => ({
        index,
        key: `option${index + 1}`,
        name: group.name,
        values: (group.values || []).map((value) => (typeof value === 'string' ? value : value.name || value.value)),
        kind: this.classifyOptionGroup(group.name, index, sourceGroups.length),
      }));
      this.debug('buildOptionGroups:result', this.optionGroups);
    },

    classifyOptionGroup(name, index, total) {
      const normalized = (name || '').toLowerCase();

      if (/(serving|size|quantity|count|pack)/.test(normalized)) return 'quantity';
      if (/(flavor|flavour|taste)/.test(normalized)) return 'flavor';
      if (total > 1 && index === 0) return 'quantity';
      if (total > 1 && index === total - 1) return 'flavor';
      return 'choice';
    },

    selectInitialVariant() {
      const initialVariant =
        this.product.variants.find(
          (variant) => String(variant.id) === String(this.product.selected_or_first_available_variant_id)
        ) ||
        this.product.variants.find((variant) => variant.available) ||
        this.product.variants[0] ||
        null;

      if (!initialVariant) return;

      this.selectedVariant = this.enrichVariant(initialVariant);
      this.selectedOptions = this.optionGroups.reduce((acc, group, index) => {
        acc[group.key] = initialVariant.options?.[index] || group.values[0] || null;
        return acc;
      }, {});
      this.debug('selectInitialVariant', {
        initialVariantRaw: initialVariant,
        initialVariantEnriched: this.selectedVariant,
        selectedOptions: this.selectedOptions,
      });
    },

    enrichVariant(variant) {
      if (!variant) return null;

      const enrichedVariant = {
        ...variant,
        metafields: this.variantMetafieldsById[String(variant.id)] || {},
      };
      this.debug('enrichVariant', {
        variantId: variant.id,
        enrichedVariant,
      });
      return enrichedVariant;
    },

    getMatchingVariants(selection = this.selectedOptions) {
      const matches = (this.product?.variants || []).filter((variant) =>
        this.optionGroups.every((group, index) => {
          const expected = selection[group.key];
          return !expected || variant.options?.[index] === expected;
        })
      );
      this.debug('getMatchingVariants', {
        selection,
        matchIds: matches.map((variant) => variant.id),
      });
      return matches;
    },

    findVariantForValue(group, value) {
      const selection = { ...this.selectedOptions, [group.key]: value };
      const matches = this.getMatchingVariants(selection);
      const variant = this.enrichVariant(matches.find((variant) => variant.available) || matches[0] || null);
      this.debug('findVariantForValue', {
        group,
        value,
        selection,
        matchedVariant: variant,
      });
      return variant;
    },

    selectOption(groupKey, value) {
      this.debug('selectOption:start', {
        groupKey,
        value,
        selectedOptionsBefore: { ...this.selectedOptions },
      });
      this.selectedOptions[groupKey] = value;

      const matches = this.getMatchingVariants();
      const nextVariant = this.enrichVariant(matches.find((variant) => variant.available) || matches[0] || null);
      if (!nextVariant) {
        this.debug('selectOption:no-next-variant', {
          groupKey,
          value,
          matches,
        });
        return;
      }

      this.selectedVariant = nextVariant;
      this.syncOptionsFromVariant(nextVariant);
      this.broadcastVariantChange();
      this.showVariantSlide(nextVariant);
      this.debug('selectOption:complete', {
        selectedOptionsAfter: this.selectedOptions,
        selectedVariant: this.selectedVariant,
      });
    },

    selectVariantById(variantId) {
      const nextVariant = this.enrichVariant(
        (this.product?.variants || []).find((variant) => String(variant.id) === String(variantId)) || null
      );

      this.debug('selectVariantById:start', {
        variantId,
        nextVariant,
      });

      if (!nextVariant) return;

      this.selectedVariant = nextVariant;
      this.syncOptionsFromVariant(nextVariant);
      this.broadcastVariantChange();
      this.showVariantSlide(nextVariant);

      this.debug('selectVariantById:complete', {
        selectedOptions: this.selectedOptions,
        selectedVariant: this.selectedVariant,
      });
    },

    syncOptionsFromVariant(variant) {
      this.optionGroups.forEach((group, index) => {
        this.selectedOptions[group.key] = variant.options?.[index] || this.selectedOptions[group.key] || null;
      });
    },

    isGroupVisible(group) {
      return Array.isArray(group.values) && group.values.length > 1;
    },

    isValueSelected(groupKey, value) {
      return this.selectedOptions[groupKey] === value;
    },

    isValueAvailable(group, value) {
      const variant = this.findVariantForValue(group, value);
      return !!variant?.available;
    },

    getValueVariant(group, value) {
      return this.findVariantForValue(group, value);
    },

    getValueBadge(group, value) {
      const variant = this.getValueVariant(group, value);
      return variant?.metafields?.variant_badge || '';
    },

    getValueAdditionalText(group, value) {
      const variant = this.getValueVariant(group, value);
      return variant?.metafields?.additional_text || '';
    },

    getValueImage(group, value) {
      const variant = this.getValueVariant(group, value);
      const image = variant?.metafields?.variant_image || variant?.featured_image;
      return image?.src || image?.url || image || '';
    },

    getValueLabel(group, value) {
      return value;
    },

    getFlavorGridColumns() {
      const count = (this.product?.variants || []).length;

      if (count >= 8) return 4;
      if (count >= 6) return 3;
      if (count >= 4) return 2;
      return 2;
    },

    stickyBanner() {
      const section = document.querySelector(`[data-ctr-isolate-purchase-box-section="${this.sectionId}"]`);
      if (!section) return;

      const el = section.querySelector('.product-form__buttons.m-skio-variants.m-sticky');
      if (!el) return;

      const elchild = el.querySelector('.product-form__sticky');
      if (!elchild) return;

      const triggerOffset = el.offsetTop;
      const elementHeight = el.offsetHeight;

      const handleScroll = () => {
        const scrollY = window.scrollY || window.pageYOffset;

        if (scrollY > triggerOffset + elementHeight) {
          elchild.classList.add('m-activated');
        } else {
          elchild.classList.remove('m-activated');
        }
      };

      window.addEventListener('scroll', handleScroll, { passive: true });
      handleScroll();
      this.debug('stickyBanner:init', {
        triggerOffset,
        elementHeight,
      });
    },

    initSlider() {
      if (typeof Splide === 'undefined') {
        this.debug('initSlider:splide-missing', null);
        return;
      }

      const main = document.querySelector(`#MainSlider-${this.sectionId}`);
      const thumbs = document.querySelector(`#ThumbnailSlider-${this.sectionId}`);
      if (!main || !thumbs) {
        this.debug('initSlider:missing-dom', {
          main: !!main,
          thumbs: !!thumbs,
        });
        return;
      }

      this.mainSlider = new Splide(main, {
        type: 'slide',
        rewind: true,
        pagination: false,
        arrows: true,
        breakpoints: {
          749: { arrows: false },
        },
      });

      this.thumbnailSlider = new Splide(thumbs, {
        fixedWidth: 65,
        fixedHeight: 65,
        gap: 15,
        rewind: true,
        pagination: false,
        isNavigation: true,
        arrows: false,
        breakpoints: {
          640: { fixedWidth: 55, fixedHeight: 55, gap: 10 },
        },
      });

      this.mainSlider.sync(this.thumbnailSlider);
      this.mainSlider.mount();
      this.thumbnailSlider.mount();
      this.debug('initSlider:mounted', {
        mainSliderId: `MainSlider-${this.sectionId}`,
        thumbnailSliderId: `ThumbnailSlider-${this.sectionId}`,
      });
    },

    showVariantSlide(variant) {
      if (!this.mainSlider || !variant) return;

      const thumbs = document.querySelectorAll(
        `#ThumbnailSlider-${this.sectionId} .thumbnail-slide[data-variant-ids]`
      );

      let targetIndex = null;
      thumbs.forEach((thumb) => {
        const variantIds = (thumb.getAttribute('data-variant-ids') || '')
          .split(',')
          .map((id) => id.trim());
        if (variantIds.includes(String(variant.id))) {
          targetIndex = parseInt(thumb.dataset.index, 10);
        }
      });

      if (targetIndex !== null) {
        this.mainSlider.go(targetIndex);
      }
    },

    openModal(imgSrc) {
      this.modalOpen = true;
      this.imgModalSrc = imgSrc || '';
      document.body.classList.add('is-modal-open');
    },

    closeModal() {
      this.modalOpen = false;
      this.imgModalSrc = '';
      document.body.classList.remove('is-modal-open');
    },

    broadcastVariantChange() {
      this.debug('broadcastVariantChange', {
        variant: this.selectedVariant,
        product: this.product,
      });
      window.dispatchEvent(
        new CustomEvent('ctr-isolate-purchase-box:variant-change', {
          detail: {
            sectionId: this.sectionId,
            variant: this.selectedVariant,
            product: this.product,
          },
        })
      );
    },
  }));
});
